/**
 * Pure color-model conversions for the swatch color inspector. The backend
 * only ever hands us a CSS color string (hex / rgb() / hsl() / named); these
 * helpers parse that into RGB and project it into the models the UI offers
 * (HEX, RGB, HSL, CMYK) — entirely client-side, no backend dependency.
 *
 * Pantone is intentionally NOT supported: it's a proprietary, licensed system
 * with no accurate open RGB→Pantone mapping, so we don't fake one.
 */

export interface RGB {
  r: number;
  g: number;
  b: number;
}
export interface HSL {
  h: number;
  s: number;
  l: number;
}
export interface CMYK {
  c: number;
  m: number;
  y: number;
  k: number;
}

export interface ColorModels {
  hex: string;
  rgb: RGB;
  hsl: HSL;
  cmyk: CMYK;
}

const clamp = (n: number, min: number, max: number) => Math.min(max, Math.max(min, n));
const round = (n: number) => Math.round(n);

/** Common CSS named colors — enough to parse typical seeded swatches. */
const NAMED: Record<string, string> = {
  black: '#000000',
  white: '#ffffff',
  red: '#ff0000',
  lime: '#00ff00',
  green: '#008000',
  blue: '#0000ff',
  yellow: '#ffff00',
  cyan: '#00ffff',
  aqua: '#00ffff',
  magenta: '#ff00ff',
  fuchsia: '#ff00ff',
  silver: '#c0c0c0',
  gray: '#808080',
  grey: '#808080',
  maroon: '#800000',
  olive: '#808000',
  purple: '#800080',
  teal: '#008080',
  navy: '#000080',
  orange: '#ffa500',
  pink: '#ffc0cb',
  brown: '#a52a2a',
  gold: '#ffd700',
  beige: '#f5f5dc',
  coral: '#ff7f50',
  ivory: '#fffff0',
  indigo: '#4b0082',
  transparent: '#000000',
};

function parseHex(input: string): RGB | null {
  let h = input.replace(/^#/, '').trim();
  // Expand shorthand (#rgb / #rgba → #rrggbb…), drop any alpha channel.
  if (h.length === 3 || h.length === 4) {
    h = h
      .split('')
      .map(c => c + c)
      .join('');
  }
  if (h.length === 8) h = h.slice(0, 6); // #rrggbbaa → drop alpha
  if (h.length !== 6 || !/^[0-9a-f]{6}$/i.test(h)) return null;
  return {
    r: parseInt(h.slice(0, 2), 16),
    g: parseInt(h.slice(2, 4), 16),
    b: parseInt(h.slice(4, 6), 16),
  };
}

function parseRgbFn(input: string): RGB | null {
  // Lenient on sign / range — out-of-range channels are clamped below.
  const m = input.match(
    /^rgba?\(\s*(-?[\d.]+)\s*,\s*(-?[\d.]+)\s*,\s*(-?[\d.]+)\s*(?:,\s*[\d.]+\s*)?\)$/i,
  );
  if (!m) return null;
  return {
    r: clamp(round(Number(m[1])), 0, 255),
    g: clamp(round(Number(m[2])), 0, 255),
    b: clamp(round(Number(m[3])), 0, 255),
  };
}

function parseHslFn(input: string): RGB | null {
  const m = input.match(
    /^hsla?\(\s*(-?[\d.]+)\s*,\s*([\d.]+)%\s*,\s*([\d.]+)%\s*(?:,\s*[\d.]+\s*)?\)$/i,
  );
  if (!m) return null;
  return hslToRgb({ h: Number(m[1]), s: Number(m[2]), l: Number(m[3]) });
}

/**
 * Parse a CSS color string into RGB, or `null` when unrecognized (the caller
 * should then show the raw value). Supports hex (3/4/6/8-digit), `rgb()/rgba()`,
 * `hsl()/hsla()`, and a common set of named colors.
 */
export function parseColor(input: string | undefined | null): RGB | null {
  if (!input) return null;
  const v = input.trim().toLowerCase();
  if (!v) return null;
  if (v in NAMED) return parseHex(NAMED[v]);
  if (v.startsWith('#')) return parseHex(v);
  if (v.startsWith('rgb')) return parseRgbFn(v);
  if (v.startsWith('hsl')) return parseHslFn(v);
  // Bare hex without '#'
  return parseHex(v);
}

export function rgbToHex({ r, g, b }: RGB): string {
  const hex = (n: number) => clamp(round(n), 0, 255).toString(16).padStart(2, '0');
  return `#${hex(r)}${hex(g)}${hex(b)}`;
}

export function rgbToHsl({ r, g, b }: RGB): HSL {
  const rn = r / 255;
  const gn = g / 255;
  const bn = b / 255;
  const max = Math.max(rn, gn, bn);
  const min = Math.min(rn, gn, bn);
  const d = max - min;
  let h = 0;
  if (d !== 0) {
    if (max === rn) h = ((gn - bn) / d) % 6;
    else if (max === gn) h = (bn - rn) / d + 2;
    else h = (rn - gn) / d + 4;
    h *= 60;
    if (h < 0) h += 360;
  }
  const l = (max + min) / 2;
  const s = d === 0 ? 0 : d / (1 - Math.abs(2 * l - 1));
  return { h: round(h), s: round(s * 100), l: round(l * 100) };
}

export function hslToRgb({ h, s, l }: HSL): RGB {
  const hn = ((h % 360) + 360) % 360;
  const sn = clamp(s, 0, 100) / 100;
  const ln = clamp(l, 0, 100) / 100;
  const c = (1 - Math.abs(2 * ln - 1)) * sn;
  const x = c * (1 - Math.abs(((hn / 60) % 2) - 1));
  const m = ln - c / 2;
  let rp = 0;
  let gp = 0;
  let bp = 0;
  if (hn < 60) [rp, gp, bp] = [c, x, 0];
  else if (hn < 120) [rp, gp, bp] = [x, c, 0];
  else if (hn < 180) [rp, gp, bp] = [0, c, x];
  else if (hn < 240) [rp, gp, bp] = [0, x, c];
  else if (hn < 300) [rp, gp, bp] = [x, 0, c];
  else [rp, gp, bp] = [c, 0, x];
  return {
    r: round((rp + m) * 255),
    g: round((gp + m) * 255),
    b: round((bp + m) * 255),
  };
}

export function rgbToCmyk({ r, g, b }: RGB): CMYK {
  const rn = r / 255;
  const gn = g / 255;
  const bn = b / 255;
  const k = 1 - Math.max(rn, gn, bn);
  if (k === 1) return { c: 0, m: 0, y: 0, k: 100 }; // pure black
  const c = (1 - rn - k) / (1 - k);
  const m = (1 - gn - k) / (1 - k);
  const y = (1 - bn - k) / (1 - k);
  return { c: round(c * 100), m: round(m * 100), y: round(y * 100), k: round(k * 100) };
}

export const formatHex = (rgb: RGB): string => rgbToHex(rgb).toUpperCase();
export const formatRgb = ({ r, g, b }: RGB): string => `rgb(${r}, ${g}, ${b})`;
export const formatHsl = ({ h, s, l }: HSL): string => `hsl(${h}, ${s}%, ${l}%)`;
export const formatCmyk = ({ c, m, y, k }: CMYK): string => `cmyk(${c}%, ${m}%, ${y}%, ${k}%)`;

/**
 * Parse a CSS color and project it into all supported models at once, or
 * `null` if the input can't be parsed.
 */
export function describeColor(input: string | undefined | null): ColorModels | null {
  const rgb = parseColor(input);
  if (!rgb) return null;
  return {
    hex: formatHex(rgb),
    rgb,
    hsl: rgbToHsl(rgb),
    cmyk: rgbToCmyk(rgb),
  };
}
